/*
 * <dot-text> – animated 3D dot-matrix headline (Revotronics redesign)
 * Renders the element's text in the dot font on an offscreen canvas, detects every
 * single dot and draws it as a tiny cube. Dots fly in when the element scrolls into
 * view, then idle with a soft wave. Respects prefers-reduced-motion, pauses offscreen.
 * Attributes: depth (0.45), idle (1 = wave on, 0 = off), ms (entrance duration, 900)
 */
(function () {
  if (customElements.get('dot-text')) return;
  const SCALE = 2;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const scratch = document.createElement('canvas');

  function rgbOf(color) {
    const c = document.createElement('canvas').getContext('2d');
    c.fillStyle = color;
    const v = c.fillStyle;
    if (v.startsWith('#')) return [1, 3, 5].map((i) => parseInt(v.slice(i, i + 2), 16));
    return (v.match(/[\d.]+/g) || ['255', '255', '255']).slice(0, 3).map(Number);
  }
  function shade(rgb, f) {
    const ch = (v) => Math.round(f < 1 ? v * f : v + (255 - v) * (f - 1));
    return `rgb(${ch(rgb[0])}, ${ch(rgb[1])}, ${ch(rgb[2])})`;
  }
  function wrap(ctx, text, max) {
    const lines = [];
    let line = '';
    for (const word of text.split(/\s+/)) {
      const test = line ? line + ' ' + word : word;
      if (line && ctx.measureText(test).width > max) {
        lines.push(line);
        line = word;
      } else line = test;
    }
    if (line) lines.push(line);
    return lines;
  }
  function scatter(x, y) {
    const a = Math.random() * Math.PI * 2;
    const r = 60 + Math.random() * 150;
    return { x: x + Math.cos(a) * r, y: y + Math.sin(a) * r };
  }

  class DotText extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.text = (this.getAttribute('text') || this.textContent).replace(/\s+/g, ' ').trim();
      this.depth = parseFloat(this.getAttribute('depth') || '0.45');
      this.idle = parseFloat(this.getAttribute('idle') ?? '1');
      this.ms = parseFloat(this.getAttribute('ms') || '900');
      this.innerHTML = '';
      const read = document.createElement('span');
      read.className = 'dot-text__read';
      read.textContent = this.text;
      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.append(read, this.canvas);
      this.particles = [];
      this.cell = 5;
      this.progress = 0;
      this.frame = 0;
      this.visible = false;
      this.started = false;
      this.lastWidth = 0;

      const cs = getComputedStyle(this);
      const fontReady = document.fonts && document.fonts.load
        ? document.fonts.load(`${cs.fontWeight} ${parseFloat(cs.fontSize) || 40}px ${cs.fontFamily}`, this.text).catch(() => {})
        : Promise.resolve();
      fontReady.then(() => {
        this.layout();
        this.classList.add('is-ready');
      });

      this.ro = new ResizeObserver(() => {
        const w = this.clientWidth;
        if (w && w !== this.lastWidth) {
          this.lastWidth = w;
          if (this.classList.contains('is-ready')) this.layout();
        }
      });
      this.ro.observe(this);

      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !this.started && this.particles.length) this.start();
        if (this.visible) this.kick();
      }, { threshold: 0.15 });
      this.io.observe(this);

      this.onVis = () => this.kick();
      document.addEventListener('visibilitychange', this.onVis);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.ro && this.ro.disconnect();
      this.io && this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVis);
    }

    sample(width) {
      const cs = getComputedStyle(this);
      let fs = parseFloat(cs.fontSize) || 40;
      let lh = parseFloat(cs.lineHeight) || fs * 1.15;
      const ctx = scratch.getContext('2d', { willReadFrequently: true });
      let font = `${cs.fontWeight} ${fs}px ${cs.fontFamily}`;
      ctx.font = font;
      if ('letterSpacing' in ctx) ctx.letterSpacing = cs.letterSpacing;
      let pad = fs * 0.11 * this.depth * (1 + 0.55 * this.idle);
      const usable = Math.max(10, width - pad * 2);
      // shrink to fit: a single word must never be wider than the available width
      const widest = Math.max(...this.text.split(/\s+/).map((w) => ctx.measureText(w).width));
      if (widest > usable) {
        const k = (usable / widest) * 0.98;
        fs *= k;
        lh *= k;
        pad *= k;
        font = `${cs.fontWeight} ${fs}px ${cs.fontFamily}`;
        ctx.font = font;
        if ('letterSpacing' in ctx) ctx.letterSpacing = (parseFloat(cs.letterSpacing) || 0) * k + 'px';
      }
      const lines = wrap(ctx, this.text, usable);
      const height = Math.ceil(lines.length * lh + pad * 2);
      const W = Math.max(1, Math.ceil(width * SCALE));
      const H = Math.max(1, Math.ceil(height * SCALE));
      scratch.width = W;
      scratch.height = H;
      ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
      const spacing = ctx.letterSpacing;
      ctx.font = font;
      if ('letterSpacing' in ctx) ctx.letterSpacing = spacing;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#fff';
      const align = cs.textAlign;
      lines.forEach((line, i) => {
        const w = ctx.measureText(line).width;
        let x = align === 'center' ? (width - w) / 2 : align === 'right' || align === 'end' ? usable - w : 0;
        x = Math.max(0, Math.min(x, usable - w));
        ctx.fillText(line, x, pad + (i + 0.5) * lh);
      });
      const data = ctx.getImageData(0, 0, W, H).data;
      const seen = new Uint8Array(W * H);
      const stack = new Int32Array(W * H);
      const cells = [];
      const sizes = [];
      for (let p = 0; p < W * H; p++) {
        if (seen[p] || data[p * 4 + 3] < 128) continue;
        let top = 0;
        stack[top++] = p;
        seen[p] = 1;
        let minX = W, maxX = 0, minY = H, maxY = 0;
        while (top > 0) {
          const q = stack[--top];
          const x = q % W, y = (q / W) | 0;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          const push = (n) => {
            if (!seen[n] && data[n * 4 + 3] >= 128) { seen[n] = 1; stack[top++] = n; }
          };
          if (x > 0) push(q - 1);
          if (x < W - 1) push(q + 1);
          if (y > 0) push(q - W);
          if (y < H - 1) push(q + W);
        }
        cells.push({ x: (minX + maxX + 1) / 2 / SCALE, y: (minY + maxY + 1) / 2 / SCALE });
        sizes.push((maxX - minX + 1) / SCALE);
      }
      sizes.sort((a, b) => a - b);
      this.cell = sizes.length ? sizes[sizes.length >> 1] : Math.max(3, fs * 0.11);
      return { cells, height };
    }

    pos(p, m) {
      const t = ease(Math.min(1, Math.max(0, (m - p.wait) / (1 - p.wait))));
      return { x: p.x0 + (p.x1 - p.x0) * t, y: p.y0 + (p.y1 - p.y0) * t, t };
    }

    layout() {
      const width = this.clientWidth;
      if (!width) return;
      this.lastWidth = width;
      const rgb = rgbOf(getComputedStyle(this).color);
      this.colors = { front: shade(rgb, 0.82), top: shade(rgb, 1.18), side: shade(rgb, 0.45) };
      const { cells, height } = this.sample(width);
      this.height = height;
      this.style.height = height + 'px';
      const motion = !reduced();
      const current = this.particles.map((p) => this.pos(p, this.progress)).filter((_, i) => this.particles[i].a1 > 0);
      const from = current.sort((a, b) => a.x - b.x || a.y - b.y);
      const to = cells.slice().sort((a, b) => a.x - b.x || a.y - b.y);
      const first = !this.particles.length;
      this.particles = [];
      const n = Math.max(from.length, to.length);
      for (let i = 0; i < n; i++) {
        const a = from[i], b = to[i];
        const out = b ? null : scatter(a.x, a.y);
        const inn = scatter((b || a).x, (b || a).y);
        this.particles.push({
          x0: a ? a.x : inn.x, y0: a ? a.y : inn.y,
          x1: b ? b.x : out.x, y1: b ? b.y : out.y,
          a0: a ? 1 : 0, a1: b ? 1 : 0,
          wait: motion ? Math.random() * 0.28 : 0,
          bow: motion ? (Math.random() - 0.5) * this.cell * 4 : 0,
        });
      }
      this.progress = 0;
      if (first && !this.visible) { this.draw(0); return; }
      this.start();
    }

    start() {
      this.started = true;
      this.progress = 0;
      this.t0 = performance.now();
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame((t) => this.tick(t));
    }

    kick() {
      if (!this.frame && this.started && !document.hidden) this.frame = requestAnimationFrame((t) => this.tick(t));
    }

    tick(now) {
      const dur = reduced() ? 0 : this.ms;
      this.progress = dur ? Math.min(1, (now - this.t0) / dur) : 1;
      this.draw(now);
      const keep = this.progress < 1 || (this.idle > 0 && !reduced() && this.visible && !document.hidden);
      this.frame = keep ? requestAnimationFrame((t) => this.tick(t)) : 0;
    }

    draw(now) {
      const cv = this.canvas;
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const w = this.clientWidth, h = this.height || 0;
      if (!w || !h) return;
      if (cv.width !== Math.ceil(w * dpr) || cv.height !== Math.ceil(h * dpr)) {
        cv.width = Math.ceil(w * dpr);
        cv.height = Math.ceil(h * dpr);
        cv.style.width = w + 'px';
        cv.style.height = h + 'px';
      }
      const ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const c = this.cell, idle = reduced() ? 0 : this.idle, col = this.colors;
      const m = this.started ? this.progress : 0;
      for (const p of this.particles) {
        const r = this.pos(p, m);
        const alpha = p.a0 + (p.a1 - p.a0) * r.t;
        if (alpha <= 0.01) continue;
        ctx.globalAlpha = alpha;
        const arc = Math.sin(r.t * Math.PI);
        const wave = idle > 0 ? Math.sin((r.x + r.y) * 0.035 - now * 0.0026) : 0;
        const x = r.x - c / 2;
        const y = r.y + arc * p.bow - c / 2 + wave * c * 0.28 * idle;
        const u = c * this.depth * (1 + arc * 0.5 + wave * 0.55 * idle);
        const d = c * (1 + wave * 0.12 * idle);
        if (u > 0.2) {
          ctx.fillStyle = col.top;
          ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + u, y - u); ctx.lineTo(x + d + u, y - u); ctx.lineTo(x + d, y); ctx.fill();
          ctx.fillStyle = col.side;
          ctx.beginPath(); ctx.moveTo(x + d, y); ctx.lineTo(x + d + u, y - u); ctx.lineTo(x + d + u, y - u + d); ctx.lineTo(x + d, y + d); ctx.fill();
        }
        ctx.fillStyle = col.front;
        ctx.fillRect(x, y, d, d);
      }
      ctx.globalAlpha = 1;
    }
  }
  customElements.define('dot-text', DotText);

  // Upgrade big section headings to animated dot text (only short, plain headings)
  function enhance(root) {
    root.querySelectorAll('[data-revo-dot-heading] :is(h1, h2).rich-text__heading, [data-revo-dot-heading] :is(h1,h2).image-with-text__heading, [data-revo-dot-heading] h2.title').forEach((h) => {
      if (h.querySelector('dot-text, img, a')) return;
      const txt = h.textContent.replace(/\s+/g, ' ').trim();
      if (!txt || txt.length > 60) return;
      const el = document.createElement('dot-text');
      el.setAttribute('text', txt);
      h.textContent = '';
      h.classList.add('has-dot-text');
      h.appendChild(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhance(document));
  else enhance(document);
  document.addEventListener('shopify:section:load', (e) => enhance(e.target));
})();
